import {
  Icon,
  IconColor,
  IconName,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Link } from '@delmonicos/shared-design-system-react/src/components/link/link';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { PageTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/title';
import classNames from 'classnames';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  PageActionContextProvider,
  usePageActionContext,
} from '@shared/layout/page-layout/page-layout-action.context-provider';
import {
  PageBreadcrumbContextProvider,
  usePageBreadcrumbContext,
} from '@shared/layout/page-layout/page-layout-breadcrumb.context-provider';
import {
  PageTitleContextProvider,
  usePageTitleContext,
} from '@shared/layout/page-layout/page-layout-title.context-provider';

import * as css from './page-layout.module.scss';

function PageLayout(
  props: Readonly<React.PropsWithChildren<{}>>,
): React.JSX.Element {
  const { children } = props;

  return (
    <div className={css.pageLayout}>
      <PageBreadcrumbContextProvider>
        <PageTitleContextProvider>
          <PageActionContextProvider>{children}</PageActionContextProvider>
        </PageTitleContextProvider>
      </PageBreadcrumbContextProvider>
    </div>
  );
}

export type PageHeaderProps = {
  readonly labelKey: string;
  readonly icon: IconName;
  readonly parent?: string;
};

function PageHeader({
  icon,
  labelKey,
  parent,
}: Readonly<PageHeaderProps>): React.JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const actions = usePageActionContext();
  const title = usePageTitleContext();
  const breadcrumbLinks = usePageBreadcrumbContext();

  return (
    <Stack gap={GapEnum.XS} direction="column">
      <Stack gap={GapEnum.XS} alignItems="center">
        {breadcrumbLinks.map(({ url, label, iconName }) => (
          <Fragment key={label}>
            <Icon name={iconName} color={IconColor.Secondary} />{' '}
            <Link
              className={classNames(css.breadcrumbLink)}
              onClick={() => navigate(url)}>
              {label} /
            </Link>
          </Fragment>
        ))}
      </Stack>

      <Stack
        className={css.actions}
        justifyContent="space-between"
        direction="row"
        alignItems="center"
        gap={GapEnum.M}>
        <PageTitle
          text={title ?? t(labelKey)}
          icon={icon}
          goBack={parent ? () => navigate(-1) : undefined}
        />
        <Stack direction="row" gap={GapEnum.XS}>
          {actions}
        </Stack>
      </Stack>
    </Stack>
  );
}

PageLayout.Header = PageHeader;

export { PageLayout };
